import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '../utils/supabase';
import { SupabaseError } from '../utils/errorHandling';

export function useJobInsights() {
  const [insights, setInsights] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { userProfile } = useAuth();

  const fetchInsights = async () => {
    if (!userProfile) return;
    
    setIsLoading(true);
    try {
      // Default market demand data in case table doesn't exist
      let marketDemand = {
        in_demand_skills: ['Content Writing', 'Social Media Management', 'Project Management', 'Customer Support', 'Data Entry'],
        trending_roles: ['Virtual Executive Assistant', 'Social Media Manager', 'Content Creator']
      };
      
      try {
        // Try to get market demand skills
        const { data, error } = await supabase
          .from('job_market_analysis')
          .select('in_demand_skills, trending_roles')
          .single();
          
        // Only update if data exists and no error
        if (!error && data) {
          marketDemand = data;
        }
      } catch (marketErr) {
        console.log('Market data table may not exist yet:', marketErr);
        // Continue with default data
      }
      
      // Get job application stats
      let appStats = [];
      try {
        const { data, error } = await supabase
          .from('job_applications')
          .select('status')
          .eq('applicant_id', userProfile.id);
        
        if (!error) {
          appStats = data || [];
        }
      } catch (appErr) {
        console.log('Error fetching application stats:', appErr);
        // Continue with empty app stats
      }
      
      // Calculate profile completion
      const profileFields = [
        'full_name', 'phone', 'location', 'bio', 
        'website', 'linkedin', 'hourly_rate', 'skills',
        'experience_years', 'profile_picture'
      ];
      
      const completedFields = profileFields.filter(
        field => userProfile[field] && 
        (Array.isArray(userProfile[field]) ? 
          userProfile[field].length > 0 : 
          userProfile[field].toString().trim() !== '')
      );
      
      const completionRate = Math.round(
        (completedFields.length / profileFields.length) * 100
      );
      
      // Generate personalized insights
      const personalizedInsights = [];
      
      // Profile completion insight
      if (completionRate < 100) {
        personalizedInsights.push({
          type: 'profile_completion',
          title: 'Complete Your Profile',
          description: `Your profile is ${completionRate}% complete. Complete profiles get 2x more interview requests.`,
          icon: 'UserCheck',
          action: {
            label: 'Update Profile',
            url: '/va-profile-settings'
          },
          color: 'bg-purple-50 text-purple-600'
        });
      }
      
      // Skill gap analysis
      if (marketDemand?.in_demand_skills && userProfile.skills) {
        const missingSkills = marketDemand.in_demand_skills.filter(
          skill => !userProfile.skills.includes(skill)
        ).slice(0, 3);
        
        if (missingSkills.length > 0) {
          personalizedInsights.push({
            type: 'skill_gap',
            title: 'In-Demand Skills Gap',
            description: `Adding ${missingSkills.join(', ')} can increase your job matches by 30%`,
            icon: 'TrendingUp',
            action: {
              label: 'Add Skills',
              url: '/va-profile-settings'
            },
            color: 'bg-blue-50 text-blue-600'
          });
        }
      }
      
      // Application performance
      if (appStats && appStats.length > 0) {
        const rejectionRate = appStats.filter(app => 
          app.status === 'rejected').length / appStats.length;
          
        if (rejectionRate > 0.5) {
          personalizedInsights.push({
            type: 'application_improvement',
            title: 'Improve Applications',
            description: 'Your application acceptance rate is below average. Consider improving your cover letters.',
            icon: 'FileText',
            action: {
              label: 'Get Tips',
              url: '/help-support/improve-application-success-rate'
            },
            color: 'bg-amber-50 text-amber-600'
          });
        }
      }
      
      // Always provide at least one insight
      if (personalizedInsights.length === 0) {
        // Find the most common skill across all published jobs
        let topSkill = marketDemand?.in_demand_skills?.[0] || 'Social Media Management';
        try {
          const { data: publishedJobs, error: jobsError } = await supabase
            .from('jobs')
            .select('required_skills')
            .eq('status', 'published');

          if (!jobsError && publishedJobs && publishedJobs.length > 0) {
            const skillCount = {};
            publishedJobs.forEach(job => {
              const skills = job.required_skills || [];
              skills.forEach(skill => {
                if (skill) skillCount[skill] = (skillCount[skill] || 0) + 1;
              });
            });
            const sorted = Object.entries(skillCount).sort(([, a], [, b]) => b - a);
            if (sorted.length > 0) topSkill = sorted[0][0];
          }
        } catch (trendErr) {
          console.log('Could not fetch trending skill from jobs:', trendErr);
        }

        personalizedInsights.push({
          type: 'market_trend',
          title: 'Market Trend',
          description: `"${topSkill}" is the most in-demand skill across active job listings right now.`,
          icon: 'TrendingUp',
          action: {
            label: 'Explore',
            url: `/job-search?skills=${encodeURIComponent(topSkill)}`
          },
          color: 'bg-green-50 text-green-600'
        });
      }
      
      setInsights(personalizedInsights);
      setError(null);
    } catch (err) {
      console.error('Error fetching job insights:', err);
      setError(new SupabaseError(err.message, err).getUserFriendlyMessage());
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInsights();
  }, [userProfile]);
  
  const refreshInsights = () => {
    fetchInsights();
  };
  
  return { insights, isLoading, error, refreshInsights };
}